import { formatShortDate } from '../../appointments/date';
import type {
    VirtualAppointmentStatus,
    VirtualAppointmentTeam,
    VirtualAppointmentTriggerKind
} from './types';

// Display-only projection shared by every read-only VA surface.
export interface VirtualAppointmentSummary {
    key: string;
    /** The type code (Vrefi, Vrem, …) or the custom text: what the team and DM notes use. */
    typeTitle: string;
    /** The lookup's descriptive label, for listed types whose label differs from the code. */
    typeLabel?: string;
    triggerKind: VirtualAppointmentTriggerKind;
    triggerAligner: number | null;
    triggerWeeks: number | null;
    /** The day a "from now" week counts from; null when it counts from the treatment start. */
    weeksAnchorDay: string | null;
    /** The day a weeks trigger falls due, when the day it counts from is known. */
    dueDate: string | null;
    triggerNogos: number | null;
    /** No-gos counted so far; null when unknown, as for a draft. */
    nogoCount: number | null;
    team: VirtualAppointmentTeam;
    status: VirtualAppointmentStatus;
    description: string | null;
}

export const VIRTUAL_APPOINTMENT_STATUS_LABELS: Record<VirtualAppointmentStatus, string> = {
    planned: 'Planned',
    open: 'Open',
    waiting_patient: 'Waiting on patient',
    waiting_doctor: 'Waiting on doctor',
    done: 'Done',
    canceled: 'Canceled'
};

// Full, untruncated title for aria-labels and tooltips: "Vrefi, Refinement check".
export function fullTypeTitle(summary: VirtualAppointmentSummary, separator = ', '): string {
    return summary.typeLabel
        ? `${summary.typeTitle}${separator}${summary.typeLabel}`
        : summary.typeTitle;
}

function nogoNoun(count: number): string {
    return count === 1 ? 'no-go' : 'no-gos';
}

// "1 no-go" / "3 no-gos"; "? no-gos" while the threshold is unset.
export function formatNogosCount(nogos: number | null): string {
    return nogos === null ? '? no-gos' : `${nogos} ${nogoNoun(nogos)}`;
}

// "3 no-gos", or "3 no-gos (1 so far)" when the running count is known.
function formatNogosTrigger(nogos: number | null, nogoCount: number | null): string {
    const threshold = formatNogosCount(nogos);
    return nogos !== null && nogoCount !== null ? `${threshold} (${nogoCount} so far)` : threshold;
}

export const OPENED_NOW_LABEL = 'Opened now';

export function formatWeeksTrigger(weeks: number | null): string {
    return weeks !== null ? `Week ${weeks}` : 'Week ?';
}

// "6 weeks from Oct 1, 2026": a week counted from the VA's own anchor rather than the treatment start.
export function formatAnchoredWeeksTrigger(weeks: number | null, anchorDay: string): string {
    const count = weeks === null ? '? weeks' : `${weeks} ${weeks === 1 ? 'week' : 'weeks'}`;
    return `${count} from ${formatShortDate(anchorDay)}`;
}

function formatSummaryWeeks(summary: VirtualAppointmentSummary): string {
    if (summary.weeksAnchorDay !== null) {
        return formatAnchoredWeeksTrigger(summary.triggerWeeks, summary.weeksAnchorDay);
    }
    const week = formatWeeksTrigger(summary.triggerWeeks);
    return summary.dueDate ? `${week} (${formatShortDate(summary.dueDate)})` : week;
}

// "Aligner 14", "Week 6 (Oct 12, 2026)" once the due day is known, "6 weeks from Oct 1, 2026",
// "3 no-gos (1 so far)" while planned, or "Opened now".
export function formatSummaryTrigger(summary: VirtualAppointmentSummary): string {
    switch (summary.triggerKind) {
        case 'aligner':
            return summary.triggerAligner !== null
                ? `Aligner ${summary.triggerAligner}`
                : 'Aligner ?';
        case 'weeks':
            return formatSummaryWeeks(summary);
        case 'nogos':
            return formatNogosTrigger(
                summary.triggerNogos,
                summary.status === 'planned' ? summary.nogoCount : null
            );
        case 'now':
            return OPENED_NOW_LABEL;
    }
}

export function isFrozenSummary(summary: VirtualAppointmentSummary): boolean {
    return summary.status !== 'planned';
}
